<script lang="ts">
  // lib
  import * as _ from "lodash";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { DealTierProductEntity } from "@shared/services/event_tracking/entities";

  // components
  import UIRouterLink from "@shared/svelte/Components/UIRouterLink.svelte";
  import FormatMoney from "@shared/svelte/Components/FormatMoney.svelte";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import QuantityDealText from "@shared/svelte/Components/Deal/QuantityDealText.svelte";

  // types
  import type { CartProduct } from "@shared/types/ProductLine";
  import type { SimpleCartDiscount } from "@shared/types/Deals";
  import { DISCOUNT_AMOUNT_TYPES } from "@shared/types/Deals";

  // props
  export let cartProduct: CartProduct;
  export let showNextTier = true;

  // functions
  function trackDealTierProductImpression(product: CartProduct, discount: SimpleCartDiscount) {
    const entity: DealTierProductEntity = new DealTierProductEntity({
      product_id: product.product_id,
      deal_tier_id: discount.deal_tier_id,
      distributor_id: product.distributor_id,
      product_group_id: discount.product_group_id,
      sku: product.sku,
    });
    trackEvent("begin_to_render", [entity]);
  }

  function getDiscountInfo() {
    cartProduct = cartProduct;

    if (showNextTier) {
      nextmmDiscount = cartProduct.discounts.find(
        orderProductDiscount =>
          orderProductDiscount.next_discount?.is_mix_match &&
          orderProductDiscount.next_discount?.amount_type !== DISCOUNT_AMOUNT_TYPES.RIP,
      )?.next_discount;
    } else {
      nextmmDiscount = null;
    }

    upsellQuantityDeal = cartProduct.discounts.find(
      d => d.next_discount && !d.next_discount?.is_mix_match,
    );
  }

  function hasEligibleNextDiscount(discount) {
    const nextDiscount = discount.next_discount;
    if (!nextDiscount) return false;

    return nextDiscount.amount_type === DISCOUNT_AMOUNT_TYPES.DOLLARS_PER_CASE;
  }

  function logImpressions() {
    if (nextmmDiscount && nextmmDiscount.deal_tier_id !== lastNextImpressionId) {
      debounceNextDealTierImpression(cartProduct, nextmmDiscount);
      lastNextImpressionId = nextmmDiscount.deal_tier_id;
    }
  }

  // reactives
  $: {
    if (cartProduct.discounts) {
      getDiscountInfo();
      logImpressions();
    }
  }

  // data
  const { trackEvent } = EventTracking;

  let nextmmDiscount, upsellQuantityDeal;
  let debounceNextDealTierImpression = _.debounce(trackDealTierProductImpression, 250);

  let lastNextImpressionId = null;
</script>

{#if upsellQuantityDeal && hasEligibleNextDiscount(upsellQuantityDeal)}
  <div
    class="fizz-flex-row-4 fizz-align-content-start fizz-surface-gray fizz-inset-square-4 fizz-radius-medium fizz-text-subdued"
    data-discount-id={upsellQuantityDeal.next_discount.id}
    data-testid="discount-deal-text"
    style="align-items: flex-start"
  >
    <span style="position: relative; top: 3px;" class="fizz-text-subdued"
      ><Icon width="12" height="12" type="DealSatisfied" className="fizz-icon-12" /></span
    >

    <div class="fizz-text-small">
      <p class="fizz-text-subdued fizz-no-wrap">
        <strong>
          <QuantityDealText
            {cartProduct}
            discount={upsellQuantityDeal.next_discount}
            discount_id={upsellQuantityDeal.next_discount.id}
            futureDeal={true}
            disableQTYText={true}
            useLegacyLayout={false}
          />
        </strong>
      </p>
    </div>
  </div>
{/if}

{#if nextmmDiscount && nextmmDiscount.amount > 0}
  <div
    class="fizz-flex-row-4 fizz-align-content-start fizz-surface-gray fizz-inset-square-4 fizz-radius-medium"
    data-deal-tier-id={nextmmDiscount.deal_tier_id}
  >
    <span style="position: relative; top: 3px;" class="fizz-text-subdued">
      <Icon width="12" height="12" type="DealSatisfied" className="fizz-icon-12" />
    </span>

    <div class="fizz-text-small">
      <p class="fizz-text-subdued fizz-no-wrap">
        {#if nextmmDiscount.product_group_id}
          <strong>
            <UIRouterLink
              stateName="layout.deals_v2"
              params={{
                distributorId: cartProduct.distributor_id,
                productGroupId: nextmmDiscount.product_group_id,
              }}
              trackingParams={{
                discountClick: true,
                distributor_id: cartProduct.distributor_id,
                product_group_id: nextmmDiscount.product_group_id,
                product_id: cartProduct.product_id,
                sku: cartProduct.sku,
              }}
            >
              <u data-testid="deal-breakdown"
                >{nextmmDiscount.case_count}
                {nextmmDiscount.deal_tier_label} mix deal</u
              >
            </UIRouterLink>
            available (-<FormatMoney
              input={nextmmDiscount.amount}
            />/{nextmmDiscount.deal_tier_label})
          </strong>
        {:else}
          <strong>
            <UIRouterLink
              stateName="layout.deal_detail"
              classes="fizz-text-subdued"
              params={{ dealId: nextmmDiscount.deal_id, dealTierId: nextmmDiscount.deal_tier_id }}
              trackingParams={{
                discountClick: true,
                product_id: (cartProduct.product || {}).id,
                deal_tier_id: nextmmDiscount.deal_tier_id,
              }}
            >
              <u data-testid="deal-breakdown"
                >{nextmmDiscount.case_count}
                {nextmmDiscount.deal_tier_label} mix deal</u
              >
            </UIRouterLink>
            available (-<FormatMoney
              input={nextmmDiscount.amount}
            />/{nextmmDiscount.deal_tier_label})
          </strong>
        {/if}
      </p>
    </div>
  </div>
{/if}
